/**
 * Income calculation Cloud Function client
 * Calls server-side income calculation instead of client-side
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';

interface CalculateLeadIncomeRequest {
  leadId: string;
}

interface LeadIncomeBreakdown {
  leadId: string;
  leadName: string;
  loanType: string;
  lender: string;
  
  disbursedAmount: number;
  bankPayoutPercentage: number;
  totalBankPayout: number;
  
  companyPayoutPercentage: number;
  companyIncome: number;
  
  businessHeadPayoutPercentage: number;
  businessHeadGrossAmount: number;
  
  vendorPayoutPercentage: number;
  vendorPayoutAmount: number;
  
  netAfterVendor: number;
  
  assignedUserId?: string;
  assignedUserName: string;
  userEmploymentType: 'freelance' | 'salaried';
  
  freelancePayoutPercentage?: number;
  freelanceGrossIncome?: number;
  freelanceNetIncome?: number;
  freelanceBHShare?: number;
  
  employeeRevenueShare?: number;
  businessHeadRevenueShare?: number;
  
  finalEmployeeIncome: number;
  finalBusinessHeadIncome: number;
  finalCompanyIncome: number;
  
  disbursementMonth: string;
  disbursementDate: Date;
  
  isManuallyAdjusted: boolean;
}

interface CalculateLeadIncomeResponse {
  success: boolean;
  breakdown: LeadIncomeBreakdown;
}

/**
 * Calculate income for a disbursed lead (server-side)
 * @param leadId - Lead ID to calculate income for
 * @returns LeadIncomeBreakdown
 */
export async function calculateLeadIncomeServer(leadId: string): Promise<LeadIncomeBreakdown> {
  try {
    const calculateFn = httpsCallable<CalculateLeadIncomeRequest, CalculateLeadIncomeResponse>(
      functions,
      'calculateLeadIncome'
    );
    
    const result = await calculateFn({ leadId });
    
    if (!result.data.success) {
      throw new Error('Income calculation failed on server');
    }
    
    return result.data.breakdown;
  } catch (error: any) {
    console.error('Error calling income calculation function:', error);
    
    // Extract error message from Firebase Functions error
    if (error.code && error.message) {
      throw new Error(`Income calculation failed: ${error.message}`);
    }
    
    throw error;
  }
}
